/**
 * Sub-spec 09 Plan 8 — /settings/integrations/zapier (outbound webhooks)
 *
 * Lists webhook rows. Create form picks an event from the closed registry,
 * pastes a target URL, and shows the one-time secret on creation. Rotate
 * button regenerates the secret (and one-time-displays it again).
 */
import { useConfirm } from '@/components/Confirm';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface WebhookRow {
  id: string;
  eventType: string;
  targetUrl: string;
  secretMasked: string;
  active: boolean;
  lastDeliveredAt: string | null;
  lastStatusCode: number | null;
  lastFailureReason: string | null;
  failureCount: number;
  createdAt: string;
}

interface OneTimeSecret {
  webhookId: string;
  secret: string;
}

export function WebhooksSettings() {
  const [rows, setRows] = useState<WebhookRow[]>([]);
  const [events, setEvents] = useState<string[]>([]);
  const [eventType, setEventType] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [oneTime, setOneTime] = useState<OneTimeSecret | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { confirm, ConfirmDialog } = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [hooksRes, eventsRes] = await Promise.all([
        fetch('/api/outbound-webhooks', { credentials: 'include' }),
        fetch('/api/outbound-webhooks/events', { credentials: 'include' }),
      ]);
      if (hooksRes.ok) {
        const body = (await hooksRes.json()) as { webhooks: WebhookRow[] };
        setRows(body.webhooks);
      }
      if (eventsRes.ok) {
        const body = (await eventsRes.json()) as { events: string[] };
        setEvents(body.events);
        if (!eventType && body.events[0]) setEventType(body.events[0]);
      }
    } finally {
      setLoading(false);
    }
  }, [eventType]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = useCallback(async () => {
    if (!eventType || !targetUrl) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/outbound-webhooks', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ eventType, targetUrl }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as { error?: string; issues?: string[] };
        setError(err?.issues?.join(', ') ?? err?.error ?? 'Create failed.');
        return;
      }
      const body = (await res.json()) as {
        webhook: { id: string; eventType: string };
        secret: string;
      };
      setOneTime({ webhookId: body.webhook.id, secret: body.secret });
      setTargetUrl('');
      await load();
    } finally {
      setSaving(false);
    }
  }, [eventType, targetUrl, load]);

  const rotate = useCallback(
    async (id: string) => {
      const yes = await confirm({
        title: 'Rotate webhook secret?',
        message:
          'Rotating the secret invalidates existing subscribers until they update their HMAC verifier.',
        confirmLabel: 'Rotate secret',
        danger: true,
      });
      if (!yes) return;
      const res = await fetch(`/api/outbound-webhooks/${id}/rotate-secret`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        setError('Rotate failed.');
        return;
      }
      const body = (await res.json()) as { id: string; secret: string };
      setOneTime({ webhookId: body.id, secret: body.secret });
      await load();
    },
    [load, confirm],
  );

  const remove = useCallback(
    async (id: string) => {
      const yes = await confirm({
        title: 'Delete webhook?',
        message: 'Deliveries to this endpoint will stop immediately.',
        confirmLabel: 'Delete',
        danger: true,
      });
      if (!yes) return;
      const res = await fetch(`/api/outbound-webhooks/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) await load();
    },
    [load, confirm],
  );

  if (loading) return <div className="py-4 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6">
      <ConfirmDialog />

      <header>
        <h1 className="font-heading text-2xl font-medium tracking-tight">
          Zapier / outbound webhooks
        </h1>
        <p className="text-sm text-muted-foreground">
          Send platform events to Zapier (or any HMAC-aware subscriber). The platform signs every
          POST with the webhook's secret using HMAC-SHA256.
        </p>
      </header>

      {error ? (
        <div className="rounded-md bg-warning/10 border border-warning px-3 py-2 text-sm text-warning">
          {error}
        </div>
      ) : null}

      {oneTime ? (
        <div className="rounded-md bg-success/10 border border-success px-3 py-2 text-sm">
          <p className="font-medium">Secret for webhook {oneTime.webhookId.slice(0, 8)}</p>
          <pre className="num mt-1 text-xs overflow-x-auto">{oneTime.secret}</pre>
          <p className="text-xs text-muted-foreground mt-1">
            Copy this now — it will not be shown again.
          </p>
          <button type="button" className="text-xs underline mt-1" onClick={() => setOneTime(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      <section className="rounded-lg border border-border bg-card p-4 space-y-2">
        <h2 className="text-sm font-medium">Add webhook</h2>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
          <select
            className="md:col-span-4 text-sm rounded-md border border-border px-3 py-2"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
          >
            {events.map((ev) => (
              <option key={ev} value={ev}>
                {ev}
              </option>
            ))}
          </select>
          <input
            className="md:col-span-7 text-sm rounded-md border border-border px-3 py-2"
            type="url"
            placeholder="https://hooks.zapier.com/hooks/catch/..."
            value={targetUrl}
            onChange={(e) => setTargetUrl(e.target.value)}
          />
          <button
            type="button"
            className="md:col-span-1 text-sm rounded-md bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
            onClick={() => void create()}
            disabled={saving || !eventType || !targetUrl}
          >
            Add
          </button>
        </div>
      </section>

      <ul className="space-y-2">
        {rows.length === 0 ? (
          <li className="text-sm text-muted-foreground">No webhooks configured.</li>
        ) : null}
        {rows.map((r) => (
          <li
            key={r.id}
            className="rounded-lg border border-border bg-card p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-center"
          >
            <div className="md:col-span-3 text-sm">
              <div className="font-mono text-xs">{r.eventType}</div>
              <div className="text-xs text-muted-foreground">
                {r.active ? 'Active' : 'Disabled'}
              </div>
            </div>
            <div className="md:col-span-6 text-xs break-all">{r.targetUrl}</div>
            <div className="md:col-span-3 flex items-center justify-end gap-1">
              <button type="button" className="text-xs underline" onClick={() => void rotate(r.id)}>
                Rotate secret
              </button>
              <button
                type="button"
                className="text-xs text-warning underline"
                onClick={() => void remove(r.id)}
              >
                Delete
              </button>
            </div>
            {r.lastFailureReason ? (
              <div className="md:col-span-12 text-xs text-warning">
                Last failure: {r.lastFailureReason} (status {r.lastStatusCode ?? '?'},{' '}
                {r.failureCount} fails total)
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      <Link to="/settings/integrations" className="text-sm underline">
        Back to integrations
      </Link>
    </div>
  );
}
